import { create } from "zustand";
import { combine } from "zustand/middleware";

type MobileEdgeChromeState = {
  menuOverlayOpenCount: number;
  /** When true: narrow viewport + main slide-out menu is expanded (drawer visible). Hides inspector tab behind it. */
  mobileMainMenuExpanded: boolean;
  /** Narrow viewport only: fullscreen library/terrain drag from panel — toolbar and panels hide for unobstructed scene view. */
  immersiveLibraryDragActive: boolean;
  /**
   * Narrow viewport: a main-menu tool sheet or expanded inspector is open (same condition as hiding the
   * collapsed main-menu tab) — hide left-edge reopen tabs so the stack is not cluttered.
   */
  mobilePanelCoversEdgeChrome: boolean;
};

type MobileEdgeChromeActions = {
  notifyMenuOverlayOpenChange: (open: boolean) => void;
  setMobileMainMenuExpanded: (expanded: boolean) => void;
  setImmersiveLibraryDragActive: (active: boolean) => void;
  setMobilePanelCoversEdgeChrome: (covers: boolean) => void;
};

export const useMobileEdgeChromeStore = create<
  MobileEdgeChromeState & { actions: MobileEdgeChromeActions }
>()(
  combine(
    {
      menuOverlayOpenCount: 0,
      mobileMainMenuExpanded: false,
      immersiveLibraryDragActive: false,
      mobilePanelCoversEdgeChrome: false,
    },
    (set, get) => ({
      actions: {
        notifyMenuOverlayOpenChange: (open) =>
          set({
            menuOverlayOpenCount: Math.max(0, get().menuOverlayOpenCount + (open ? 1 : -1)),
          }),
        setMobileMainMenuExpanded: (mobileMainMenuExpanded) => set({ mobileMainMenuExpanded }),
        setImmersiveLibraryDragActive: (immersiveLibraryDragActive) =>
          set({ immersiveLibraryDragActive }),
        setMobilePanelCoversEdgeChrome: (mobilePanelCoversEdgeChrome) =>
          set({ mobilePanelCoversEdgeChrome }),
      },
    })
  )
);

/**
 * True when left-edge tabs should hide (menu overlays, expanded main drawer, immersive drag,
 * or a main-menu tool sheet / expanded inspector — same distraction as an open main-menu popup).
 */
export function useMobileEdgeChromeSuppressed() {
  return useMobileEdgeChromeStore(
    (s) =>
      s.menuOverlayOpenCount > 0 ||
      s.mobileMainMenuExpanded ||
      s.immersiveLibraryDragActive ||
      s.mobilePanelCoversEdgeChrome
  );
}
